import React from "react";
import "@/App.css";
import "katex/dist/katex.min.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AuthPage from "@/pages/AuthPage";
import { PublicHome, PricingPage, FeaturesPage, HowItWorksPage, AboutPage, PrivacyPage } from "@/pages/PublicSite";
import AuthCallback from "@/pages/AuthCallback";
import ChatPage from "@/pages/ChatPage";
import PrivateMemoryPage from "@/pages/PrivateMemoryPage";
import PracticePage from "@/pages/PracticePage";
import ClassesPage from "@/pages/ClassesPage";
import ClassDetailPage from "@/pages/ClassDetailPage";
import BillingPage from "@/pages/PlansPage";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentPages";
import { Locked } from "@/components/Paywall";
import GamePage from "@/pages/GamePage";
import HomePage from "@/pages/HomePage";
import ArtifactsPage from "@/pages/ArtifactsPage";
import HelpPage from "@/pages/HelpPage";
import ChatsPage from "@/pages/ChatsPage";
import FlashcardsPage from "@/pages/FlashcardsPage";
import { AccountPage, SettingsPage } from "@/pages/AccountPages";
import ExamsPage from "@/pages/ExamsPage";
import { AppShell } from "@/components/AppShell";
import { RestoreAccount } from "@/components/RestoreAccount";
import { effectiveTheme } from "@/lib/theme";
import StaffLayout, { Gate } from "@/pages/staff/StaffLayout";
import StaffVerifyReturn from "@/pages/staff/StaffVerifyReturn";
import OverviewPage from "@/pages/staff/OverviewPage";
import StudentsPage from "@/pages/staff/StudentsPage";
import StudentDetailPage from "@/pages/staff/StudentDetailPage";
import UsageBillingPage from "@/pages/staff/UsageBillingPage";
import ErrorsPage from "@/pages/staff/ErrorsPage";
import TeamPage from "@/pages/staff/TeamPage";
import StaffMemberPage from "@/pages/staff/StaffMemberPage";
import AuditPage from "@/pages/staff/AuditPage";

const STAFF_VERIFY_PATH = "/staff/verify";

const Loader = () => (
  <div className="h-screen flex items-center justify-center text-neutral-500 text-sm" data-testid="app-loading">Loading…</div>
);

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.deletion) return <RestoreAccount />;
  return children;
}

function ShortLink() {
  const { short } = useParams();
  if (short.length !== 1) return <Navigate to="/app" replace />;
  return <Navigate to={`/?utm_source=heycatch&utm_campaign=${encodeURIComponent(short)}`} replace />;
}

function AppRouter() {
  const location = useLocation();
  const firstPage = React.useRef(true);
  React.useEffect(() => {
    if (firstPage.current) { firstPage.current = false; return; }
    window.ttq?.page();
  }, [location.pathname]);
  if (location.hash?.includes("session_id=") && location.pathname !== STAFF_VERIFY_PATH) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<AuthPage />} />
      <Route path="/" element={<PublicHome />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<HomePage />} />
        <Route path="new" element={<ChatPage />} />
        <Route path="c/:chatId" element={<ChatPage />} />
        <Route path="chats" element={<ChatsPage />} />
        <Route path="artifacts" element={<ArtifactsPage />} />
        <Route path="flashcards" element={<Locked feature="practice"><FlashcardsPage /></Locked>} />
        <Route path="help" element={<HelpPage />} />
        <Route path="help/:slug" element={<HelpPage />} />
        <Route path="account" element={<AccountPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="memory" element={<PrivateMemoryPage />} />
        <Route path="practice" element={<PracticePage />} />
        <Route path="progress" element={<Navigate to="/app" replace />} />
        <Route path="groups" element={<ClassesPage />} />
        <Route path="groups/:classId" element={<ClassDetailPage />} />
        <Route path="plans" element={<Navigate to="/app/billing" replace />} />
        <Route path="diagrams" element={<Navigate to="/app/artifacts" replace />} />
        <Route path="saved" element={<Navigate to="/app/artifacts" replace />} />
        <Route path="exams" element={<ExamsPage />} />
        <Route path="exams/:examId" element={<ExamsPage />} />
        <Route path="games/:gameId" element={<GamePage />} />
        <Route path="admin/*" element={<Navigate to="/staff/billing" replace />} />
      </Route>
      <Route path={STAFF_VERIFY_PATH} element={<Protected><StaffVerifyReturn /></Protected>} />
      <Route path="/staff" element={<Protected><StaffLayout /></Protected>}>
        <Route index element={<OverviewPage />} />
        <Route path="students" element={<Gate perm="students.view"><StudentsPage /></Gate>} />
        <Route path="students/:uid" element={<Gate perm="students.view"><StudentDetailPage /></Gate>} />
        <Route path="billing" element={<Gate perm="billing.view"><UsageBillingPage /></Gate>} />
        <Route path="errors" element={<Gate perm="errors.view"><ErrorsPage /></Gate>} />
        <Route path="team" element={<Gate perm="staff.manage"><TeamPage /></Gate>} />
        <Route path="team/:uid" element={<Gate perm="staff.manage"><StaffMemberPage /></Gate>} />
        <Route path="audit" element={<Gate perm="audit.view"><AuditPage /></Gate>} />
        <Route path="*" element={<Navigate to="/staff" replace />} />
      </Route>
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />
      <Route path="/payment/cancel" element={<Protected><PaymentCancel /></Protected>} />
      <Route path="/:short" element={<ShortLink />} />
      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}

function ThemedToaster() {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const light = pathname.startsWith("/app") && !!user && effectiveTheme(user.theme) === "light";
  return <Toaster theme={light ? "light" : "dark"} position="top-right" />;
}

export default function App() {
  return (
    <div className="App grain">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <ThemedToaster />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
